import { Button, Flex, Group, ScrollArea, Stack, Text, Timeline, Title } from '@mantine/core';
import { IconCircle, IconDownload } from '@tabler/icons-react';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';

import { Batch, BatchTraceActionEnum, EORIGIN_DATE_FORMAT_WITH_TIME, User } from '@e-origin/shared';

import { Select } from '@components';
import { useDownloadTraceFileFromCloud } from '@hooks/batch';
import { useUsersByDeclarant } from '@hooks/useUsersByDeclarant';

interface BatchAuditProps {
  batch: Batch;
  onHide: () => void;
}

interface SelectedTrace {
  action: string;
  dateTime: Date;
  index: number;
  user?: Partial<User>;
  cloudBucketFileName?: string;
  fileName?: string;
}

export const BatchAudit = ({ batch }: BatchAuditProps) => {
  const { data: usersList } = useUsersByDeclarant({ declarantId: batch.declarant });
  const [selectedTrace, setSelectedTrace] = useState<SelectedTrace | null>(null);
  const [actionFilter, setActionFilter] = useState<string | null>(null);

  const downloadTraceFileFromCloud = useDownloadTraceFileFromCloud();

  const filteredTraces = useMemo(() => {
    return cloneDeep(batch.trace)
      .filter((t) => t.action === actionFilter || !actionFilter)
      .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())
      .map((trace) => ({
        action: trace.action,
        dateTime: trace.dateTime,
        user: usersList?.find((user) => user._id === trace.userId),
        cloudBucketFileName: trace.dataChanges?.find((change) => change.fieldName === 'cloudBucketFileName')?.oldFieldValue,
        fileName: trace.dataChanges?.find((change) => change.fieldName === 'filename')?.oldFieldValue,
      }));
  }, [batch.trace, actionFilter, usersList]);

  const actionOptions = Object.keys(BatchTraceActionEnum).map((key) => ({
    value: BatchTraceActionEnum[key],
    label: BatchTraceActionEnum[key],
  }));

  useEffect(() => {
    if (filteredTraces.length > 0) {
      handleTraceChange(0);
    } else {
      setSelectedTrace(null);
    }
  }, [filteredTraces]);

  const handleTraceChange = (index: number) => {
    setSelectedTrace({
      ...filteredTraces[index],
      index,
    });
  };

  const handleDownloadFile = (trace: SelectedTrace) => {
    downloadTraceFileFromCloud.mutate({
      cloudBucketFileName: trace.cloudBucketFileName,
      fileName: trace.fileName,
    });
  };

  return (
    <Flex gap={20} align="flex-start">
      <Stack gap="md">
        <Select
          label="Filter by Action"
          placeholder="Select action"
          data={actionOptions}
          value={actionFilter}
          onChange={setActionFilter}
          clearable
          style={{ width: 200 }}
        />
        <ScrollArea h={400} type="auto">
          <Timeline active={selectedTrace?.index ?? -1} bulletSize={24} lineWidth={2} color="blue" miw={250}>
            {filteredTraces.map((trace, index) => (
              <Timeline.Item
                key={`${trace.dateTime.toString()}-${index}`}
                bullet={<IconCircle size={12} />}
                onClick={() => handleTraceChange(index)}
                title={
                  <Stack
                    fw={selectedTrace?.index === index ? 'bold' : 'normal'}
                    c={selectedTrace?.index === index ? 'blue' : 'inherit'}
                    style={{ cursor: 'pointer' }}
                    gap={1}
                  >
                    <Text size="sm">{moment(trace.dateTime).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
                    <Text size="sm">{trace.action}</Text>
                  </Stack>
                }
              />
            ))}
          </Timeline>
        </ScrollArea>
      </Stack>

      {selectedTrace && (
        <Stack gap="xl">
          <Title order={4}>Trace details</Title>
          <Stack gap={8}>
            <Group gap="xs">
              <Text fw={500}>Date:</Text>
              <Text>{moment(selectedTrace.dateTime).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
            </Group>

            <Group gap="xs">
              <Text fw={500}>Action:</Text>
              <Text>{selectedTrace.action}</Text>
            </Group>

            <Group gap="xs">
              <Text fw={500}>User: </Text>
              <Text>
                {selectedTrace.user?.firstName} {selectedTrace.user?.lastName}
              </Text>
            </Group>

            {selectedTrace.cloudBucketFileName && (
              <>
                <Group gap="xs">
                  <Text fw={500}>Old Filename:</Text>
                  <Text>{selectedTrace.fileName}</Text>
                </Group>
                <Button size="lg" variant="outline" leftSection={<IconDownload size={16} />} onClick={() => handleDownloadFile(selectedTrace)}>
                  Download old file
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      )}
    </Flex>
  );
};
